<script lang="ts">
  /**
   * Rita-tabb (S-04-17 + S-04-17c-part1 + UX-fix 2026-05-05).
   *
   * Konsoliderar tidigare separata tabbar (Karta + Rundor + POI) till
   * en yta. Två modes:
   *  - **Karta** (default): översikts-MapLibre-karta med rundor + POI
   *    som lager. Härd-segment-rendering kommer i part2.
   *  - **Lista**: per-lager admin-CRUD (HearthsView / SegmentsView /
   *    PoisView). Lager-väljaren styr aktiv sub-vy.
   *
   * UX-fixar 2026-05-05 (Jonas-feedback + harden-ux P0):
   * - Mode-toggle (Karta/Lista) flyttad till header-line, höger sida —
   *   tydlig segmented control som primär kontroll inom Rita.
   * - Lead-text borttagen — sub-vyerna är självförklarande och
   *   header/lager-knapp ger nog kontext.
   * - Lager-toolbar kompakt (mindre knappar, ingen sub-text) så den
   *   visuellt underordnar sig huvudtabbarna.
   * - Sub-vyerna (HearthsView/SegmentsView/PoisView) har egna headers
   *   borttagna i denna iteration eftersom de bara rendereras inuti
   *   Rita; lager-knappens text fungerar som rubrik.
   *
   * UX-fix 2026-05-05 (Jonas-feedback uppföljning på V1):
   * - `+` (Skapa) och Refresh lyfta från sub-vyerna till layer-toolbar-
   *   raden, högerställda. Knapparna är synliga oavsett Karta/Lista —
   *   det är aktivt lager (runda/härd/POI) som styr vad `+`:et gör.
   * - Karta-mode-beteende: klick på `+` eller Refresh i Karta-mode
   *   växlar automatiskt till Lista-mode för aktivt lager OCH triggar
   *   action — användaren ser direkt resultatet av sin handling.
   * - Trigger-pattern: signal-counter (`createTrigger`/`refreshTrigger`)
   *   som ökas vid klick. Sub-vyerna lyssnar via $effect och kallar sin
   *   befintliga `startCreate`/`loadRecords`.
   *
   * Klick-på-karta-för-att-skapa kommer i S-04-17c-part2.
   */
  import HearthsView from '../hearths/HearthsView.svelte';
  import PoisView from '../pois/PoisView.svelte';
  import SegmentsView from '../hearth-segments/SegmentsView.svelte';
  import RitaMapView from './RitaMapView.svelte';

  type Layer = 'runda' | 'segment' | 'poi';
  type Mode = 'karta' | 'lista';

  type Props = {
    onUnauthorized?: () => void;
    initialCenter?: { lng: number; lat: number };
    initialZoom?: number;
  };
  const { onUnauthorized, initialCenter, initialZoom }: Props = $props();

  let activeMode: Mode = $state('karta');
  let activeLayer: Layer = $state('runda');

  // Signal-counter-mönster (UX-fix 2026-05-05): Rita-layer-toolbar äger nu
  // `+` (Skapa) och Refresh-knapparna. Sub-vyerna lyssnar på counter-
  // change via $effect och triggar sin egen startCreate/loadRecords. Ett
  // initialvärde 0 ska INTE auto-trigga create — sub-vyerna håller
  // egen `lastSeen`-räknare och ignorerar matchande värden.
  let createTrigger: number = $state(0);
  let refreshTrigger: number = $state(0);

  // Karta-mode-bryggor till RitaMapView (UX-fix 2026-05-05):
  //  - addPoiTrigger: lifted "+" startar map-click-to-place-flödet på karta
  //    (bevarar UX:en där admin pickar punkt på karta direkt) — endast när
  //    activeLayer=poi.
  //  - addSegmentTrigger: lifted "+" startar pick-runda + två-punkter-
  //    flödet för att skapa härd-segment direkt på karta (Jonas-feedback
  //    2026-05-05: "härden skall välja en sträcka via en karta").
  //  - mapRefreshTrigger: lifted Refresh laddar om RitaMapView's data utan
  //    att växla till Lista.
  let mapAddPoiTrigger: number = $state(0);
  let mapAddSegmentTrigger: number = $state(0);
  let mapRefreshTrigger: number = $state(0);

  function selectMode(mode: Mode): void {
    activeMode = mode;
  }

  function selectLayer(layer: Layer): void {
    activeLayer = layer;
  }

  /**
   * Karta-mode-beteende:
   *  - activeLayer=poi: "+" triggar RitaMapView's map-click-to-place utan
   *    att växla till Lista (POI:s naturliga UX).
   *  - activeLayer=segment: "+" triggar RitaMapView's pick-runda + två-
   *    punkter-flöde — admin väljer runda direkt på kartan och klickar
   *    start/slut för segmentet (Jonas-feedback 2026-05-05).
   *  - activeLayer=runda: ingen map-create-yta i karta-mode, så vi växlar
   *    till Lista och öppnar create-modal där.
   *
   * Refresh i karta-mode triggar RitaMapView:s refresh direkt utan att
   * växla (kart-data bör laddas om utan att tappa kontext).
   */
  function handleCreate(): void {
    if (activeMode === 'karta' && activeLayer === 'poi') {
      mapAddPoiTrigger = mapAddPoiTrigger + 1;
      return;
    }
    if (activeMode === 'karta' && activeLayer === 'segment') {
      mapAddSegmentTrigger = mapAddSegmentTrigger + 1;
      return;
    }
    if (activeMode === 'karta') {
      // Karta → Lista-växling: deferr increment med queueMicrotask så
      // sub-vyen hinner mounta + $effect:s första körning fångar
      // pre-increment-värdet som "initial" innan trigger-bumpas.
      // Annars äter sub-vyen klicket eftersom första effect-runs sätter
      // lastSeen=post-increment, vilket gör increment-jämförelsen "lika"
      // och startCreate kallas aldrig (Jonas-incident 2026-05-05:
      // "man måste välja två gånger").
      activeMode = 'lista';
      queueMicrotask(() => {
        createTrigger = createTrigger + 1;
      });
      return;
    }
    createTrigger = createTrigger + 1;
  }

  function handleRefresh(): void {
    if (activeMode === 'karta') {
      mapRefreshTrigger = mapRefreshTrigger + 1;
      return;
    }
    refreshTrigger = refreshTrigger + 1;
  }

  /**
   * Dynamiskt aria-label/title på `+` baserat på aktivt lager — det är
   * ju sidan/lagret som väljer vad `+`:et skall göra (Jonas-feedback).
   */
  let createLabel = $derived(
    activeLayer === 'runda'
      ? 'Skapa runda'
      : activeLayer === 'segment'
        ? 'Skapa härd-segment'
        : 'Skapa POI',
  );
</script>

<section class="rita" aria-label="Rita">
  <header class="rita-header">
    <div class="mode-toggle" role="tablist" aria-label="Vy-mode">
      <button
        type="button"
        role="tab"
        class="mode-btn"
        class:active={activeMode === 'karta'}
        aria-selected={activeMode === 'karta'}
        onclick={() => selectMode('karta')}
      >
        <span class="mode-icon" aria-hidden="true">▦</span>
        Karta
      </button>
      <button
        type="button"
        role="tab"
        class="mode-btn"
        class:active={activeMode === 'lista'}
        aria-selected={activeMode === 'lista'}
        onclick={() => selectMode('lista')}
      >
        <span class="mode-icon" aria-hidden="true">≡</span>
        Lista
      </button>
    </div>
  </header>

  <div class="layer-toolbar">
    <div class="layer-tabs" role="tablist" aria-label="Rit-lager">
      <button
        type="button"
        role="tab"
        class="layer-btn"
        class:active={activeLayer === 'runda'}
        aria-selected={activeLayer === 'runda'}
        aria-label="Rundor"
        title="Rundor"
        onclick={() => selectLayer('runda')}
      >
        <svg
          class="layer-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <ellipse cx="12" cy="12" rx="9" ry="6" />
        </svg>
      </button>
      <button
        type="button"
        role="tab"
        class="layer-btn"
        class:active={activeLayer === 'segment'}
        aria-selected={activeLayer === 'segment'}
        aria-label="Härd-segment"
        title="Härd-segment"
        onclick={() => selectLayer('segment')}
      >
        <svg
          class="layer-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2.5"
          fill="none"
          stroke-linecap="round"
        >
          <path d="M3 12h3" />
          <path d="M9 12h3" />
          <path d="M15 12h3" />
          <path d="M21 12h0" />
        </svg>
      </button>
      <button
        type="button"
        role="tab"
        class="layer-btn"
        class:active={activeLayer === 'poi'}
        aria-selected={activeLayer === 'poi'}
        aria-label="POI"
        title="POI (Points of Interest)"
        onclick={() => selectLayer('poi')}
      >
        <svg
          class="layer-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
        </svg>
      </button>
    </div>

    <div class="layer-actions">
      <button
        type="button"
        class="icon-btn icon-btn-primary"
        onclick={handleCreate}
        aria-label={createLabel}
        title={createLabel}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2.5"
          fill="none"
          stroke-linecap="round"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
      <button
        type="button"
        class="icon-btn icon-btn-secondary"
        onclick={handleRefresh}
        aria-label="Uppdatera lista"
        title="Uppdatera"
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      </button>
    </div>
  </div>

  {#if activeMode === 'karta'}
    <RitaMapView
      {initialCenter}
      {initialZoom}
      {onUnauthorized}
      addPoiTrigger={mapAddPoiTrigger}
      addSegmentTrigger={mapAddSegmentTrigger}
      refreshTrigger={mapRefreshTrigger}
    />
  {:else}
    <div class="layer-content" role="tabpanel">
      {#if activeLayer === 'runda'}
        <HearthsView
          {onUnauthorized}
          {initialCenter}
          {initialZoom}
          {createTrigger}
          {refreshTrigger}
        />
      {:else if activeLayer === 'segment'}
        <SegmentsView {onUnauthorized} {createTrigger} {refreshTrigger} />
      {:else if activeLayer === 'poi'}
        <PoisView {onUnauthorized} {createTrigger} {refreshTrigger} />
      {/if}
    </div>
  {/if}
</section>

<style>
  .rita {
    display: grid;
    gap: 1rem;
  }
  .rita-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .mode-toggle {
    display: inline-flex;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 0.2rem;
    gap: 0.15rem;
  }
  .mode-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-dim);
    padding: 0.4rem 0.85rem;
    font-family: inherit;
    font-size: 0.9rem;
    cursor: pointer;
  }
  .mode-btn.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .mode-btn:hover:not(.active) {
    color: var(--text);
  }
  .mode-icon {
    font-size: 0.85rem;
    line-height: 1;
  }
  /* Layer-toolbar: lager-tabbar vänster, action-knappar (+/refresh)
     höger. flex-wrap så det går till två rader på smal skärm istället
     för att klippas. */
  .layer-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
  .layer-tabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .layer-actions {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .layer-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    padding: 0.45rem;
    font-family: inherit;
    font-size: 0.85rem;
    cursor: pointer;
    width: 2.4rem;
    height: 2.4rem;
  }
  .layer-btn.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .layer-btn:hover:not(.active) {
    color: var(--text);
    border-color: var(--text-dim);
  }
  /* Layer-icon fyller knappen 100% (minus padding) — samma som .icon-btn
     svg så alla 5 knapparna i toolbar:n får visuellt samma ikon-storlek
     (Jonas-feedback 2026-05-05: "ha inte olika storlekar på ikonerna på
     samma sida"). */
  .layer-icon {
    width: 100%;
    height: 100%;
    display: block;
  }

  /* Ikon-knappar — samma mönster som etablerats i HearthsView. Storlek
     2.4rem matchar layer-btn så `[runda] [härd] [POI] [+] [refresh]`
     ligger på samma rad i samma höjd. */
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    cursor: pointer;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0.45rem;
    font-family: inherit;
    transition:
      color 120ms ease,
      border-color 120ms ease,
      background 120ms ease;
  }
  .icon-btn svg {
    width: 100%;
    height: 100%;
    display: block;
  }
  .icon-btn:hover:not(:disabled) {
    color: var(--text);
    border-color: var(--text-dim);
  }
  .icon-btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .icon-btn-primary {
    color: var(--bg);
    background: var(--accent);
    border-color: var(--accent);
  }
  .icon-btn-primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
    color: var(--bg);
  }
  .icon-btn-secondary {
    color: var(--accent);
    border-color: var(--accent);
  }
  .icon-btn-secondary:hover:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }

  .layer-content {
    display: grid;
    gap: 1rem;
  }
</style>
